// react
import styled from 'styled-components';
import { css } from 'styled-components';
import { Link } from 'react-router-dom';

// components
import { Icon, Icons } from '@/components/atoms/Icon';

/**
 * NavBarMobile shows the mobile navigation bar.
 */
export const NavBarMobile = ({
  className,
  links,
  currentPath = '/',
}: NavBarMobileProps) => {
  return (
    <Wrapper className={className}>
      {links?.map((linkItem) =>
        linkItem.to ? (
          <StyledLink
            key={linkItem.to}
            to={linkItem.to}
            onClick={() => {
              if (linkItem.onClick) {
                linkItem.onClick();
              }
            }}
            $isSelected={currentPath === linkItem.to}
          >
            <StyledIcon
              icon={linkItem.icon}
              fontSize={24}
              type={currentPath === linkItem.to ? 'fas' : 'fal'}
              $isSelected={currentPath === linkItem.to}
            />
            <Label>{linkItem.label}</Label>
          </StyledLink>
        ) : (
          <StyledLinkAction
            key={linkItem.label}
            onClick={linkItem.onClick}
            $isSelected={currentPath === linkItem.to}
          >
            <StyledIcon
              icon={linkItem.icon}
              fontSize={24}
              type={currentPath === linkItem.to ? 'fas' : 'fal'}
              $isSelected={currentPath === linkItem.to}
            />
            <Label>{linkItem.label}</Label>
          </StyledLinkAction>
        ),
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: ${(props) => props.theme.color.neutral[10]};
  box-shadow: ${(props) => props.theme.elevation.navBar};
  overflow: visible;
  height: 100%;
`;

interface StyledIconProps {
  $isSelected?: boolean;
}
const StyledIcon = styled(Icon)<StyledIconProps>`
  color: ${(props) => props.theme.color.neutral[70]};
  ${(props) =>
    props.$isSelected &&
    css`
      color: ${props.theme.color.primary[80]};
    `}
`;

interface StyledLinkProps {
  $isSelected?: boolean;
}
const StyledLink = styled(Link)<StyledLinkProps>`
  all: unset;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.spacing[4]};
  color: ${(props) => props.theme.color.neutral[70]};
  padding: ${(props) => props.theme.spacing[12]};
  user-select: none;
  cursor: pointer;
  flex: 1;
  border-top: 4px solid transparent;

  transition-duration: 0.2s;
  ${(props) =>
    props.$isSelected &&
    css`
      border-top: 4px solid ${props.theme.color.primary[80]};
      color: ${props.theme.color.primary[80]};
      pointer-events: none;
    `}
`;

const StyledLinkAction = styled.div<StyledLinkProps>`
  all: unset;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.spacing[4]};
  color: ${(props) => props.theme.color.neutral[70]};
  padding: ${(props) => props.theme.spacing[12]};
  user-select: none;
  cursor: pointer;
  flex: 1;
  border-top: 4px solid transparent;

  transition-duration: 0.2s;
  ${(props) =>
    props.$isSelected &&
    css`
      border-top: 4px solid ${props.theme.color.primary[80]};
      color: ${props.theme.color.primary[80]};
      pointer-events: none;
    `}
`;

const Label = styled.div`
  font: ${(props) => props.theme.typography.body.xxs.semibold};
  text-align: center;
  user-select: none;
`;

interface LinkItem {
  label: string;
  icon: Icons;
  to?: string;
  onClick?: () => void;
}

interface NavBarMobileProps {
  className?: string;
  links?: LinkItem[];
  currentPath?: string;
}

NavBarMobile.displayName = 'NavBarMobile';

export default NavBarMobile;
